import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';

import { useViewerControl } from 'libs/rendering/gltf/hooks';

import { NavCubeConfig } from 'libs/rendering/gltf/config';

import Tooltip from 'components/Tooltip';

import {
  NavCubeStyled,
  ButtonStyled,
  NavCubeExpandStyled,
  NodeTitle,
  IconStyled,
  NodeStyled,
  ActionNodeStyled,
} from './navCube.styled';

const NavCube = observer(function _NavCube(props) {
  const viewerControl = useViewerControl();

  const [expanded, setExpanded] = useState(false);

  function toggleMenu() {
    setExpanded(!expanded);
  }

  const viewer = viewerControl?.viewer;
  const { settings, engine, navCube } = viewer
    ? viewerControl?.viewer
    : [null, null, null];
  const orbitEnum = viewer?.module.CameraOrbit;
  const orbitIx = settings?.GetOrbit();
  const camera = settings?.CurrentCamera();
  const tabLoadingStatus = camera?.GetLoadStatus();

  let isFacetedBody = false;
  if (camera?.IsInstance()) {
    const instance = camera?.GetInstance();
    const brep = instance?.GetBody()?.GetBrep();
    if (!brep) {
      isFacetedBody = true;
    }
  }

  // up axis
  function toggleUpAxis() {
    settings.ToggleUp();
    // eslint-disable-next-line default-case
    if (settings.IsUpZ()) {
      if (!settings.IsUpZ()) {
        settings.ToggleUp();
      }
    } else if (settings.IsUpZ()) {
      settings.ToggleUp();
    }
  }

  // camera orbit mode
  function setCameraOrbit(cameraEnum) {
    const enumValue = cameraEnum ? cameraEnum.value : -1;
    if (orbitIx !== enumValue) {
      // value changed, apply it
      switch (cameraEnum) {
        case orbitEnum.FixedUp:
          settings.SetOrbitFixedUp();
          break;
        case orbitEnum.Free:
          settings.SetOrbitFree();
          break;
        case orbitEnum.FixedX:
          settings.SetOrbitFixedX();
          break;
        case orbitEnum.FixedY:
          settings.SetOrbitFixedY();
          break;
        case orbitEnum.FixedZ:
          settings.SetOrbitFixedZ();
          break;
        default:
          break;
      }
    } else {
      // same value, clear it
      settings.ClearOrthoView(false);
    }
  }

  function isNavCubeFixed() {
    return (
      orbitIx !== orbitEnum.Free &&
      orbitIx !== orbitEnum.FixedUp &&
      orbitIx !== orbitEnum.FixedX &&
      orbitIx !== orbitEnum.FixedY &&
      orbitIx !== orbitEnum.FixedZ
    );
  }

  // "standard" orthogonal views
  function setOrthoView(newOrbitEnum) {
    const enumValue = newOrbitEnum ? newOrbitEnum.value : -1;
    if (orbitIx !== enumValue) {
      // value changed, apply it
      switch (newOrbitEnum) {
        case orbitEnum.OrthoTop:
          settings.SetOrthoTop();
          break;
        case orbitEnum.OrthoBottom:
          settings.SetOrthoBottom();
          break;
        case orbitEnum.OrthoFront:
          settings.SetOrthoFront();
          break;
        case orbitEnum.OrthoRear:
          settings.SetOrthoRear();
          break;
        case orbitEnum.OrthoLeft:
          settings.SetOrthoLeft();
          break;
        case orbitEnum.OrthoRight:
          settings.SetOrthoRight();
          break;
        default:
          break;
      }
    } else {
      // same value, clear it
      settings.ClearOrthoView(false);
    }
  }

  function highlightNavcube(newOrbitEnum) {
    const enumValue = newOrbitEnum ? newOrbitEnum.value : -1;
    viewerControl.viewer._wasm.SyncNavCubeHighlight(enumValue);
  }

  // solid boundary mode
  function updateSolidBoundary(boudnaryIx) {
    switch (boudnaryIx) {
      case 1: // solid (faces & edges)
        engine.SetSolidView();
        break;
      case 2: // wireframe
        if (!engine.IsWireframeView()) {
          engine.SetWireframeView();
        } else {
          engine.SetSolidView();
        }
        break;
      case 3: // edges
        if (!engine.IsEdgeView()) {
          engine.SetEdgesView();
        } else {
          engine.SetSolidView();
        }
        break;
      case 4: // faces
        if (!engine.IsFaceView()) {
          engine.SetFacesView();
        } else {
          engine.SetSolidView();
        }
        break;
      default:
        // solid (faces & edges)
        engine.SetSolidView();
        break;
    }
  }

  const topPosition = () => {
    return NavCubeConfig.height;
  };

  // color plots
  const currTessFieldType = camera?.GetCurrTessField();
  const fieldModeEnum = viewer?.module.TessFieldType;

  const plotTessField = type => {
    if (camera) {
      // switch to plot
      camera?.SetCurrTessField(type);
      // froce viewer render
      viewerControl?.cadFile?.assembly?.TriggerStateChange();
    }
  };

  const availableFields = [];
  if (camera?.HasTessField(fieldModeEnum.TessFieldSharpEdges)) {
    availableFields.push(
      <ActionNodeStyled
        key='Edges'
        active={true}
        selected={currTessFieldType === fieldModeEnum.TessFieldSharpEdges}
        onClick={() => plotTessField(fieldModeEnum.TessFieldSharpEdges)}
      >
        {'Edge Sharpness'}
      </ActionNodeStyled>,
    );
  }
  if (camera?.HasTessField(fieldModeEnum.TessFieldThickness)) {
    availableFields.push(
      <ActionNodeStyled
        key='Thickness'
        active={true}
        selected={currTessFieldType === fieldModeEnum.TessFieldThickness}
        onClick={() => plotTessField(fieldModeEnum.TessFieldThickness)}
      >
        {'Thickness'}
      </ActionNodeStyled>,
    );
  }
  if (camera?.HasTessField(fieldModeEnum.TessFieldOverHang)) {
    availableFields.push(
      <ActionNodeStyled
        key='Overhang'
        active={true}
        selected={currTessFieldType === fieldModeEnum.TessFieldOverHang}
        onClick={() => plotTessField(fieldModeEnum.TessFieldOverHang)}
      >
        {'Overhang'}
      </ActionNodeStyled>,
    );
  }

  useEffect(() => {
    if (navCube && viewerControl.viewerReady) {
      navCube.SetOffsetY(NavCubeConfig.height - NavCubeConfig.size);
      navCube.SetOffsetX(0.5 * (NavCubeConfig.width - NavCubeConfig.size));
    }
  }, [navCube, viewerControl.viewerReady]);

  if (!navCube) return null;

  return (
    <>
      {camera && camera.Is3D() && tabLoadingStatus === 0 && (
        <NavCubeStyled
          top={topPosition()}
          right={props.right}
          width={NavCubeConfig.width}
          height={NavCubeConfig.height}
          isExpanded={expanded}
          visible={navCube.visible && viewerControl.viewerVisible}
        >
          <Tooltip delayShow={500} place='bottom' id='navCubeTooltip' />
          <ButtonStyled
            data-intercom-target='Rendering-Modes Button'
            size='default'
            variant='tertiary'
            active={expanded}
            data-for='navCubeTooltip'
            data-tip='View settings'
            icon='camera'
            onClick={e => toggleMenu(e)}
            hasDropdown
            appearance='plain'
          />
          <ButtonStyled
            size='default'
            variant='tertiary'
            data-for='navCubeTooltip'
            data-tip='Toggle up-axis'
            icon={settings.IsUpZ() ? 'axis_z' : 'axis_y'}
            iconViewBox='0 0 40 40'
            onClick={e => toggleUpAxis(e)}
            appearance='plain'
          />
          {expanded && (
            <NavCubeExpandStyled>
              {/* Standard Views */}
              <NodeStyled>
                <NodeTitle isFirst>
                  <IconStyled
                    name='standard_view'
                    size='default'
                    viewBox='0 0 40 40'
                  />
                  Views
                </NodeTitle>

                <ActionNodeStyled
                  active={true}
                  selected={orbitIx === orbitEnum.OrthoTop}
                  onClick={() => setOrthoView(orbitEnum.OrthoTop)}
                  onMouseEnter={() => highlightNavcube(orbitEnum.OrthoTop)}
                  onMouseLeave={() => highlightNavcube(orbitEnum.Free)}
                  stacked={2}
                >
                  {'Top'}
                </ActionNodeStyled>
                <ActionNodeStyled
                  active={true}
                  selected={orbitIx === orbitEnum.OrthoBottom}
                  onClick={() => setOrthoView(orbitEnum.OrthoBottom)}
                  onMouseEnter={() => highlightNavcube(orbitEnum.OrthoBottom)}
                  onMouseLeave={() => highlightNavcube(orbitEnum.Free)}
                  stacked={2}
                >
                  {'Bottom'}
                </ActionNodeStyled>

                <ActionNodeStyled
                  active={true}
                  selected={orbitIx === orbitEnum.OrthoFront}
                  onClick={() => setOrthoView(orbitEnum.OrthoFront)}
                  onMouseEnter={() => highlightNavcube(orbitEnum.OrthoFront)}
                  onMouseLeave={() => highlightNavcube(orbitEnum.Free)}
                  stacked={2}
                >
                  {'Front'}
                </ActionNodeStyled>
                <ActionNodeStyled
                  active={true}
                  selected={orbitIx === orbitEnum.OrthoRear}
                  onClick={() => setOrthoView(orbitEnum.OrthoRear)}
                  onMouseEnter={() => highlightNavcube(orbitEnum.OrthoRear)}
                  onMouseLeave={() => highlightNavcube(orbitEnum.Free)}
                  stacked={2}
                >
                  {'Rear'}
                </ActionNodeStyled>

                <ActionNodeStyled
                  active={true}
                  selected={orbitIx === orbitEnum.OrthoLeft}
                  onClick={() => setOrthoView(orbitEnum.OrthoLeft)}
                  onMouseEnter={() => highlightNavcube(orbitEnum.OrthoLeft)}
                  onMouseLeave={() => highlightNavcube(orbitEnum.Free)}
                  stacked={2}
                >
                  {'Left'}
                </ActionNodeStyled>
                <ActionNodeStyled
                  active={true}
                  selected={orbitIx === orbitEnum.OrthoRight}
                  onClick={() => setOrthoView(orbitEnum.OrthoRight)}
                  onMouseEnter={() => highlightNavcube(orbitEnum.OrthoRight)}
                  onMouseLeave={() => highlightNavcube(orbitEnum.Free)}
                  stacked={2}
                >
                  {'Right'}
                </ActionNodeStyled>
              </NodeStyled>

              {/* Camera rotations */}
              <NodeStyled>
                <NodeTitle>
                  <IconStyled
                    name='camera'
                    size='default'
                    viewBox='0 0 25 25'
                  />
                  Orbit
                </NodeTitle>

                <ActionNodeStyled
                  active={!isNavCubeFixed()}
                  selected={orbitIx === orbitEnum.FixedUp}
                  onClick={() => setCameraOrbit(orbitEnum.FixedUp)}
                  stacked={2}
                >
                  {'Fix-Up'}
                </ActionNodeStyled>
                <ActionNodeStyled
                  active={!isNavCubeFixed()}
                  selected={orbitIx === orbitEnum.Free}
                  onClick={() => setCameraOrbit(orbitEnum.Free)}
                  stacked={2}
                >
                  {'Free'}
                </ActionNodeStyled>
                <ActionNodeStyled
                  active={!isNavCubeFixed()}
                  selected={orbitIx === orbitEnum.FixedX}
                  onClick={() => setCameraOrbit(orbitEnum.FixedX)}
                  stacked={3}
                >
                  {'Fix-X'}
                </ActionNodeStyled>
                <ActionNodeStyled
                  active={!isNavCubeFixed()}
                  selected={orbitIx === orbitEnum.FixedY}
                  onClick={() => setCameraOrbit(orbitEnum.FixedY)}
                  stacked={3}
                >
                  {'Fix-Y'}
                </ActionNodeStyled>
                <ActionNodeStyled
                  active={!isNavCubeFixed()}
                  selected={orbitIx === orbitEnum.FixedZ}
                  onClick={() => setCameraOrbit(orbitEnum.FixedZ)}
                  stacked={3}
                >
                  {'Fix-Z'}
                </ActionNodeStyled>
              </NodeStyled>

              {/* Solid Boundary */}
              <NodeStyled>
                <NodeTitle>
                  <IconStyled
                    name='render'
                    size='default'
                    viewBox='0 0 40 40'
                  />
                  Display
                </NodeTitle>
                <ActionNodeStyled
                  active={true}
                  selected={!engine.GetMeshVisible() && engine.IsSolidView()}
                  onClick={() => {
                    updateSolidBoundary(1);
                    engine.SetMeshVisible(false);
                  }}
                  stacked={2}
                >
                  {'Default'}
                </ActionNodeStyled>
                {!isFacetedBody ? (
                  <>
                    <ActionNodeStyled
                      active={true}
                      selected={engine.IsFaceView()}
                      onClick={() => updateSolidBoundary(4)}
                      stacked={2}
                    >
                      {'Faces'}
                    </ActionNodeStyled>
                    <ActionNodeStyled
                      active={true}
                      selected={engine.IsWireframeView()}
                      onClick={() => updateSolidBoundary(2)}
                      stacked={2}
                    >
                      {'Frame'}
                    </ActionNodeStyled>
                    <ActionNodeStyled
                      active={true}
                      selected={engine.IsEdgeView()}
                      onClick={() => updateSolidBoundary(3)}
                      stacked={2}
                    >
                      {'Edges'}
                    </ActionNodeStyled>
                  </>
                ) : (
                  <>
                    <ActionNodeStyled
                      active={true}
                      selected={engine.GetMeshVisible() && engine.IsSolidView()}
                      onClick={() => {
                        engine.SetMeshVisible(true);
                        updateSolidBoundary(1);
                      }}
                      stacked={2}
                    >
                      {'Mesh'}
                    </ActionNodeStyled>
                    <ActionNodeStyled
                      active={true}
                      selected={
                        engine.GetMeshVisible() && engine.IsWireframeView()
                      }
                      onClick={() => {
                        engine.SetMeshVisible(true);
                        updateSolidBoundary(2);
                      }}
                      stacked={2}
                    >
                      {'Frame'}
                    </ActionNodeStyled>
                    <ActionNodeStyled
                      active={true}
                      selected={engine.GetMeshVisible() && engine.IsEdgeView()}
                      onClick={() => {
                        engine.SetMeshVisible(true);
                        updateSolidBoundary(3);
                      }}
                      stacked={2}
                    >
                      {'Edges'}
                    </ActionNodeStyled>
                  </>
                )}
              </NodeStyled>

              {/* Gradient Plots */}
              {availableFields.length > 0 && (
                <NodeStyled>
                  <NodeTitle>
                    <IconStyled
                      name='collection'
                      size='default'
                      viewBox='0 0 24 24'
                    />
                    Color PLots
                  </NodeTitle>

                  <ActionNodeStyled
                    active={true}
                    selected={currTessFieldType === fieldModeEnum.TessFieldNone}
                    onClick={() => plotTessField(fieldModeEnum.TessFieldNone)}
                  >
                    {'None'}
                  </ActionNodeStyled>

                  {availableFields}
                </NodeStyled>
              )}
            </NavCubeExpandStyled>
          )}
        </NavCubeStyled>
      )}
    </>
  );
});

export default NavCube;
